import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { useStock } from '../contexts/StockContext';

const AddProductForm = () => {
  const [productType, setProductType] = useState('battery');
  const [formData, setFormData] = useState({
    serial: '',
    brand: '',
    capacity: '',
    bmsOrType: '',
    coulomb: 'มีสาย',
    condition: 'new',
    entryDate: '',
    note: '',
  });

  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [currentImportType, setCurrentImportType] = useState(''); // ประเภทสินค้าปัจจุบันที่กำลัง import
  const [previewHeaders, setPreviewHeaders] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [modalEntryDate, setModalEntryDate] = useState('');
  const batteryFileInputRef = useRef(null);
  const chargerFileInputRef = useRef(null);

  // ใช้ context สำหรับบันทึกข้อมูล
  const { createBatteryStock, createChargerStock, loading } = useStock();

  // ฟังก์ชันแปลงรูปแบบวันที่สำหรับแสดงผล (YYYY-MM-DD)
  const formatDateForDisplay = (dateStr) => {
    // รองรับ Excel serial number (เช่น 45982)
    const tryExcelSerial = (val) => {
      if (val === null || val === undefined) return '';
      const num = typeof val === 'number' ? val : Number(String(val).trim());
      if (!Number.isFinite(num)) return '';
      // ช่วงค่าที่สมเหตุผลสำหรับวันที่ (Excel serial ของปี 1970 คือ ~25569)
      if (num < 25569 || num > 60000) return '';
      const millis = (num - 25569) * 86400000; // แปลงเป็น millis ตั้งแต่ Unix epoch
      const d = new Date(millis);
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, '0');
      const da = String(d.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${da}`;
    };

    // ลองตีความเป็น Excel serial ก่อน
    const excel = tryExcelSerial(dateStr);
    if (excel) return excel;

    if (!dateStr) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // ทำความสะอาดข้อมูลวันที่
    const cleanDateStr = String(dateStr).trim();

    // ถ้าเป็นรูปแบบ D/M/YYYY หรือ DD/MM/YYYY (รูปแบบไทย)
    if (cleanDateStr.includes('/')) {
      const parts = cleanDateStr.split('/');
      if (parts.length === 3) {
        let day, month, year;

        // ตรวจสอบรูปแบบวันที่ - ถ้าปีอยู่ท้าย
        if (parts[2].length === 4) {
          day = parts[0].padStart(2, '0');
          month = parts[1].padStart(2, '0');
          year = parts[2];
        } else if (parts[0].length === 4) {
          // รูปแบบ YYYY/MM/DD
          year = parts[0];
          month = parts[1].padStart(2, '0');
          day = parts[2].padStart(2, '0');
        } else {
          // รูปแบบ MM/DD/YYYY (อเมริกัน)
          month = parts[0].padStart(2, '0');
          day = parts[1].padStart(2, '0');
          year = parts[2];
        }

        return `${year}-${month}-${day}`;
      }
    }

    // ถ้าเป็นรูปแบบ ISO หรือ YYYY-MM-DD แล้ว
    if (cleanDateStr.includes('-')) {
      const datePart = cleanDateStr.split('T')[0]; // ตัดเวลาออก
      const parts = datePart.split('-');
      if (parts.length === 3) {
        return `${parts[0]}-${parts[1]}-${parts[2]}`;
      }
    }

    // ถ้าเป็นรูปแบบอื่นๆ ให้ใช้วันที่ปัจจุบัน
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setProductType(newType);

    // รีเซ็ตข้อมูลฟิลด์เฉพาะตามประเภทใหม่
    setFormData({
      serial: '',
      brand: '',
      capacity: '',
      bmsOrType: '',
      coulomb: newType === 'battery' ? 'มีสาย' : '',
      condition: 'new',
      entryDate: '',
      note: '',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();



    try {
      let result;

      // เตรียมข้อมูลสำหรับบันทึก
      const dataToSave = {
        ...formData,
        status: 'พร้อมขาย', // กำหนดสถานะเริ่มต้นเป็นพร้อมขาย
        reserver: '', // ไม่มีผู้จองในตอนเริ่มต้น
        receivedDate: formData.entryDate, // ใช้ entryDate เป็น receivedDate
      };

      // Map condition เป็น condition_status สำหรับฐานข้อมูล
      if (dataToSave.condition) {
        dataToSave.condition_status = toDBConditionStatus(dataToSave.condition);
        delete dataToSave.condition;
      }

      // บันทึกข้อมูลตามประเภทสินค้า
      if (productType === 'battery') {
        result = await createBatteryStock(dataToSave);
      } else {
        result = await createChargerStock(dataToSave);
      }

      if (result.success) {
        alert(`เพิ่มข้อมูล ${productType === 'battery' ? 'แบตเตอรี่' : 'ชาร์จเจอร์'} เรียบร้อย!`);

        // ล้างฟอร์ม
        setFormData({
          serial: '',
          brand: '',
          capacity: '',
          bmsOrType: '',
          coulomb: productType === 'battery' ? 'มีสาย' : '',
          condition: 'new',
          entryDate: '',
          note: '',
        });
      } else {
        // ปรับปรุงข้อความ error ให้เข้าใจง่ายขึ้น
        let errorMessage = result.error;
        if (result.error && result.error.includes('Duplicate entry')) {
          const serialMatch = result.error.match(/Duplicate entry '([^']+)' for key 'serial'/);
          if (serialMatch) {
            errorMessage = `Serial Number "${serialMatch[1]}" มีอยู่ในระบบแล้ว กรุณาตรวจสอบข้อมูลหรือใช้ Serial Number อื่น`;
          } else {
            errorMessage = 'ข้อมูลที่ป้อนมีอยู่ในระบบแล้ว กรุณาตรวจสอบและแก้ไข';
          }
        }
        alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล: ' + errorMessage);
      }
    } catch (error) {
      // ปรับปรุงข้อความ error ให้เข้าใจง่ายขึ้น
      let errorMessage = error.message;
      if (error.message && error.message.includes('Duplicate entry')) {
        const serialMatch = error.message.match(/Duplicate entry '([^']+)' for key 'serial'/);
        if (serialMatch) {
          errorMessage = `Serial Number "${serialMatch[1]}" มีอยู่ในระบบแล้ว กรุณาตรวจสอบข้อมูลหรือใช้ Serial Number อื่น`;
        } else {
          errorMessage = 'ข้อมูลที่ป้อนมีอยู่ในระบบแล้ว กรุณาตรวจสอบและแก้ไข';
        }
      }
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล: ' + errorMessage);
    }
  };

  // ==================== ฟังก์ชันสำหรับ Import ข้อมูลแบตเตอรี่ ====================

  // ฟังก์ชันอ่านไฟล์รวม CSV/TSV/Excel และเตรียม preview + mapping
  const handleCommonFileUpload = (file, type) => {
    if (!file) return;
    const nameLower = file.name.toLowerCase();
    setCurrentImportType(type);
    setImportData([]);
    setPreviewHeaders([]);
    setPreviewRows([]);
    setColumnMapping({});

    const isExcel = nameLower.endsWith('.xlsx') || nameLower.endsWith('.xls');
    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          if (!rows || rows.length < 2) {
            alert('ไฟล์ต้องมีหัวตารางและข้อมูลอย่างน้อย 1 แถว');
            return;
          }
          const headers = rows[0].map((h) => String(h).trim()).filter((h) => h);
          const objRows = rows.slice(1).map((arr) => {
            const o = {};
            headers.forEach((h, idx) => {
              o[h] = arr[idx] !== undefined ? String(arr[idx]).trim() : '';
            });
            return o;
          }).filter((r) => Object.values(r).some((v) => v && v.trim() !== ''));
          setPreviewHeaders(headers);
          setPreviewRows(objRows);
          setColumnMapping(suggestMapping(headers, type));
          setShowImportModal(true);
        } catch (error) {
          alert('เกิดข้อผิดพลาดในการอ่านไฟล์ Excel: ' + error.message);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const { headers, rows } = parseCSVToRows(text);
          setPreviewHeaders(headers);
          setPreviewRows(rows);
          setColumnMapping(suggestMapping(headers, type));
          setShowImportModal(true);
        } catch (error) {
          alert('เกิดข้อผิดพลาดในการอ่านไฟล์: ' + error.message);
        }
      };
      reader.readAsText(file, 'UTF-8');
    }
  };

  const handleBatteryFileUpload = (event) => {
    const file = event.target.files[0];
    handleCommonFileUpload(file, 'battery');
  };

  // ฟังก์ชันสำหรับดาวน์โหลดไฟล์ตัวอย่างแบตเตอรี่
  const downloadBatterySampleCSV = () => {
    const currentDate = new Date().toISOString().split('T')[0];

    const sampleData = `serial,brand,capacity,bmsOrType,coulomb,condition,entryDate,note,reserver,receivedDate
CY-80-001-0825,Chengyi,80,มี bluetooth,มีสาย,new,${currentDate},-,-,${currentDate}
CY-80-002-0825,Chengyi,80,มี bluetooth,ไร้สาย,new,${currentDate},-,-,${currentDate}
PN-100-001-0825,Panasonic,100,onboard,มีสาย,used,${currentDate},แบตเตอรี่มือสอง,-,${currentDate}`;

    // เพิ่ม BOM (Byte Order Mark) สำหรับ UTF-8 เพื่อให้ Excel แสดงภาษาไทยถูกต้อง
    const BOM = '\uFEFF';
    const csvContent = BOM + sampleData;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_battery_import.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ==================== ฟังก์ชันสำหรับ Import ข้อมูลชาร์จเจอร์ ====================

  const handleChargerFileUpload = (event) => {
    const file = event.target.files[0];
    handleCommonFileUpload(file, 'charger');
  };

  // Utilities สำหรับ CSV และ Mapping
  const parseCSVToRows = (text) => {
    const lines = text.split('\n').filter((line) => line.trim());
    if (lines.length < 2) {
      throw new Error('ไฟล์ต้องมีหัวตารางและข้อมูลอย่างน้อย 1 แถว');
    }
    let separator = ',';
    const firstLine = lines[0];
    if (firstLine.includes('\t')) separator = '\t';
    else if (firstLine.includes(',')) separator = ',';
    else if (firstLine.includes('  ')) separator = /\s{2,}/;

    const headers = separator instanceof RegExp
      ? lines[0].split(separator).map((h) => h.trim()).filter((h) => h)
      : lines[0].split(separator).map((h) => h.trim());

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const values = separator instanceof RegExp
        ? lines[i].split(separator).map((v) => String(v).trim())
        : lines[i].split(separator).map((v) => String(v).trim());
      const hasData = values.some((v) => v && v.trim() !== '');
      if (!hasData) continue;
      const obj = {};
      headers.forEach((h, idx) => { obj[h] = values[idx] || ''; });
      rows.push(obj);
    }
    return { headers, rows };
  };

  const suggestMapping = (headers, type) => {
    const normalize = (s) => String(s || '').toLowerCase().trim();
    const find = (candidates) => {
      const cs = candidates.map(normalize);
      // หลีกเลี่ยงการจับคำที่สั้นเกินไป (เช่น 'a') ที่ทำให้แมพผิดเช่นไปจับ 'serial'
      const found = headers.find((h) => {
        const nh = normalize(h);
        // ตรงชื่อเต็ม
        if (cs.includes(nh)) return true;
        // จับแบบคำเต็ม/คำประกอบ แทนการตรวจ substring ตัวอักษรเดียว
        return cs.some((c) => {
          if (c.length <= 2) {
            // สำหรับคำสั้นๆ ให้ตรวจแบบขอบเขตคำเท่านั้น
            const regex = new RegExp(`(^|[^a-z0-9])${c}([^a-z0-9]|$)`);
            return regex.test(nh);
          }
          return nh.includes(c);
        });
      });
      return found || '';
    };
    return {
      serial: find(['serial', 'serial number', 'ซีเรียล']),
      brand: find(['brand', 'ยี่ห้อ']),
      // ลบ 'a' ออกเพื่อไม่ให้ไปแมพกับหัวคอลัมน์ใดๆ ที่มีตัว a เช่น 'serial'
      // เพิ่มคำไทย 'ความจุ' และรูปแบบที่พบบ่อย
      capacity: find(['capacity', 'ขนาด', 'ความจุ', 'ah', 'amp-hour', 'ampere-hour']),
      bmsOrType: type === 'battery' ? find(['bms', 'bms/type']) : find(['type', 'ประเภท', 'bms/type']),
      coulomb: type === 'battery' ? find(['coulomb', 'คูลอมป์']) : '',
      condition: find(['condition', 'สภาพ']),
      status: find(['status', 'สถานะ']),
      entryDate: find(['entrydate', 'entry date']),
      receivedDate: find(['receiveddate', 'received_date', 'recive_date', 'วันรับเข้า', 'รับเข้า']),
      note: find(['note', 'หมายเหตุ'])
    };
  };

  const mapRowsWithMapping = (rows, mapping, type, defaultEntryDate = '') => {
    const mapped = rows.map((r) => {
      const item = {
        serial: mapping.serial ? r[mapping.serial] || '' : '',
        brand: mapping.brand ? r[mapping.brand] || '' : '',
        capacity: mapping.capacity ? r[mapping.capacity] || '' : '',
        bmsOrType: mapping.bmsOrType ? r[mapping.bmsOrType] || '' : '',
        coulomb: mapping.coulomb ? r[mapping.coulomb] || '' : (type === 'battery' ? 'มีสาย' : ''),
        condition: mapping.condition ? r[mapping.condition] || '' : 'ใหม่',
        status: mapping.status ? r[mapping.status] || '' : 'พร้อมขาย',
        entryDate: (defaultEntryDate && String(defaultEntryDate).trim())
          ? defaultEntryDate
          : (mapping.entryDate ? r[mapping.entryDate] || '' : ''),
        receivedDate: mapping.receivedDate ? r[mapping.receivedDate] || '' : '',
        note: mapping.note ? r[mapping.note] || '' : ''
      };
      const cleaned = { ...item };
      cleaned.coulomb = fixGarbledText(cleaned.coulomb);
      cleaned.bmsOrType = fixGarbledText(cleaned.bmsOrType);
      cleaned.condition = fixGarbledText(cleaned.condition);
      cleaned.status = fixGarbledText(cleaned.status);
      return (type === 'battery' ? normalizeBatteryData(cleaned) : normalizeChargerData(cleaned));
    });
    return mapped;
  };

  // ฟังก์ชันสำหรับดาวน์โหลดไฟล์ตัวอย่างชาร์จเจอร์
  const downloadChargerSampleCSV = () => {
    const currentDate = new Date().toISOString().split('T')[0];

    const sampleData = `serial,brand,capacity,bmsOrType,coulomb,condition,entryDate,note,reserver,receivedDate
DLT-15-001-0825,Delta,15,onboard,-,new,${currentDate},-,-,${currentDate}
MW-20-001-0825,Meanwell,20,offboard,-,new,${currentDate},เครื่องชาร์จพร้อมพัดลม,-,${currentDate}
DLT-30-001-0825,Delta,30,onboard,-,used,${currentDate},เครื่องชาร์จมือสอง,-,${currentDate}`;

    // เพิ่ม BOM (Byte Order Mark) สำหรับ UTF-8 เพื่อให้ Excel แสดงภาษาไทยถูกต้อง
    const BOM = '\uFEFF';
    const csvContent = BOM + sampleData;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_charger_import.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ฟังก์ชันแปลงรูปแบบวันที่สำหรับบันทึกลงฐานข้อมูล (YYYY-MM-DD)
  const formatDateForDB = (dateStr) => {
    // Helper: current local date YYYY-MM-DD (ไม่ใช่ UTC)
    const localToday = () => {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${da}`;
    };

    if (!dateStr) return localToday();

    // รองรับ Excel serial number (เช่น 45982)
    const tryExcelSerial = (val) => {
      if (val === null || val === undefined) return '';
      const num = typeof val === 'number' ? val : Number(String(val).trim());
      if (!Number.isFinite(num)) return '';
      if (num < 25569 || num > 60000) return '';
      const millis = (num - 25569) * 86400000;
      const d = new Date(millis);
      // ใช้ local components เพื่อหลีกเลี่ยงวันถอย
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${da}`;
    };
    const excel = tryExcelSerial(dateStr);
    if (excel) return excel;

    // ทำความสะอาดข้อมูลวันที่
    const cleanDateStr = String(dateStr).trim();

    // ถ้าเป็นรูปแบบ D/M/YYYY หรือ DD/MM/YYYY (รูปแบบไทย)
    if (cleanDateStr.includes('/')) {
      const parts = cleanDateStr.split('/');
      if (parts.length === 3) {
        let day, month, year;

        // ตรวจสอบรูปแบบวันที่ - ถ้าปีอยู่ท้าย
        if (parts[2].length === 4) {
          day = parts[0].padStart(2, '0');
          month = parts[1].padStart(2, '0');
          year = parts[2];
        } else if (parts[0].length === 4) {
          // รูปแบบ YYYY/MM/DD
          year = parts[0];
          month = parts[1].padStart(2, '0');
          day = parts[2].padStart(2, '0');
        } else if (parts[1].length === 4) {
          // รูปแบบ DD/YYYY/MM (รูปแบบผิดปกติที่พบในข้อมูล)
          day = parts[0].padStart(2, '0');
          year = parts[1];
          month = parts[2].padStart(2, '0');
        } else {
          // รูปแบบ MM/DD/YYYY (อเมริกัน)
          month = parts[0].padStart(2, '0');
          day = parts[1].padStart(2, '0');
          year = parts[2];
        }

        return `${year}-${month}-${day}`;
      }
    }

    // ถ้าเป็นรูปแบบ ISO หรือ YYYY-MM-DD แล้ว
    if (cleanDateStr.includes('-')) {
      return cleanDateStr.split('T')[0]; // ตัดเวลาออก
    }

    // ถ้าเป็นรูปแบบอื่นๆ ให้ใช้วันที่ปัจจุบัน
    return localToday();
  };

  // แปลงค่า condition เป็น ENUM สำหรับฐานข้อมูล
  const toDBConditionStatus = (cond) => {
    const c = String(cond || '').trim().toLowerCase();
    if (c === 'ใหม่' || c === 'new' || c === 'brand new') return 'new';
    if (c === 'มือสอง' || c === 'ใช้แล้ว' || c === 'used') return 'used';
    if (
      c === 'refurbished' || c === 'รีเฟอร์บิช' || c === 'ปรับปรุง' ||
      c === 'ซ่อมปรับปรุง' || c === 'ซ่อมแล้ว' || c === 'เก่า'
    ) return 'refurbished';
    return 'new';
  };

  // ฟังก์ชันสำหรับ normalize ข้อมูลชาร์จเจอร์
  const normalizeChargerData = (item) => {
    const rawEntryDate = item.entryDate || item['Entry Date'] || item['วันรับเข้า'] || '';

    const normalizedItem = {
      serial: item.serial || item.Serial || item['Serial Number'] || '',
      brand: item.brand || item.Brand || item['ยี่ห้อ'] || '',
      capacity: item.capacity || item.Capacity || item['ความจุ'] || '',
      type: item.type || item.Type || item['ประเภท'] || item.bmsOrType || item.bms || item.BMS || '',
      condition: item.condition || item.Condition || item['สภาพ'] || 'ใหม่',
      status: item.status || item.Status || item['สถานะ'] || 'พร้อมขาย',
      entryDate: formatDateForDB(rawEntryDate),
      receivedDate: formatDateForDB(item.receivedDate || rawEntryDate),
      note: item.note || item.Note || item['หมายเหตุ'] || ''
    };

    // เพิ่ม bmsOrType สำหรับการแสดงผล
    normalizedItem.bmsOrType = normalizedItem.type;

    return normalizedItem;
  };



  // ฟังก์ชันสำหรับ normalize ข้อมูลแบตเตอรี่
  const normalizeBatteryData = (item) => {
    const rawEntryDate = item.entryDate || item['Entry Date'] || item['วันรับเข้า'] || '';

    const normalizedItem = {
      serial: item.serial || item.Serial || item['Serial Number'] || '',
      brand: item.brand || item.Brand || item['ยี่ห้อ'] || '',
      capacity: item.capacity || item.Capacity || item['ความจุ'] || '',
      bms: item.bms || item.BMS || item['BMS'] || item.bmsOrType || item.type || item.Type || '',
      coulomb: item.coulomb || item.Coulomb || item['คูลอมป์'] || item['Coulomb Type'] || 'มีสาย',
      condition: item.condition || item.Condition || item['สภาพ'] || 'ใหม่',
      status: item.status || item.Status || item['สถานะ'] || 'พร้อมขาย',
      entryDate: formatDateForDB(rawEntryDate),
      receivedDate: formatDateForDB(item.receivedDate || rawEntryDate),
      note: item.note || item.Note || item['หมายเหตุ'] || ''
    };

    // เพิ่ม bmsOrType สำหรับการแสดงผล
    normalizedItem.bmsOrType = normalizedItem.bms;

    return normalizedItem;
  };



  // ฟังก์ชันสำหรับแก้ไขตัวอักษรที่ผิดเพี้ยนและแปลงเป็นภาษาอังกฤษ
  const fixGarbledText = (text) => {
    if (!text) return text;

    // แปลงเป็น string และทำความสะอาด
    const cleanText = String(text).trim();

    const fixes = {
      // ตัวอักษรที่ผิดเพี้ยนแบบต่างๆ สำหรับ Coulomb - คืนค่าเป็นภาษาไทย
      '�����': 'มีสาย',
      '����': 'ไร้สาย',
      'มีสาย': 'มีสาย',
      'ไร้สาย': 'ไร้สาย',
      'มี สาย': 'มีสาย',
      'ไร้ สาย': 'ไร้สาย',
      'wired': 'มีสาย',
      'wireless': 'ไร้สาย',
      'à¸¡à¸µà¸ªà¸²à¸¢': 'มีสาย',
      'à¹à¸£à¹à¸ªà¸²à¸¢': 'ไร้สาย',
      // ตัวอักษรผิดเพี้ยนที่ผู้ใช้พบ - รูปแบบ UTF-8 ที่เสียหาย
      'เธกเธตเธชเธฒเธข': 'มีสาย',
      'เนเธเธ•เน€เธ•เธญเธฃเธตเนเธเธธเธ"เธ เธฒเธเธ"เธต': 'แบตเตอรี่คุณภาพดี',
      'เนเธฃเนเธชเธฒเธข': 'ไร้สาย',
      'เนเธเธ•เน€เธ•เธญเธฃเธตเนเธกเธทเธญเธชเธญเธ': 'แบตเตอรี่มือสอง',
      // BMS types - คืนค่าเป็นภาษาไทย
      'บลูทูธ': 'มี bluetooth',
      'มี bluetooth': 'มี bluetooth',
      'มีbluetooth': 'มี bluetooth',
      'Bluetooth': 'มี bluetooth',
      'bluetooth': 'มี bluetooth',
      '�� bluetooth': 'มี bluetooth',
      'BMS Model A': 'มี bluetooth',
      'BMS Model B': 'onboard',
      'BMS Model C': 'มี bluetooth',
      'ออนบอร์ด': 'onboard',
      'ออฟบอร์ด': 'offboard',
      'on board': 'onboard',
      'off board': 'offboard',
      'onboard': 'onboard',
      'offboard': 'offboard',
      // Status - คืนค่าเป็นภาษาไทย
      'ใหม่': 'ใหม่',
      'ของใหม่': 'ใหม่',
      'มือสอง': 'มือสอง',
      'ซ่อมแซม': 'ซ่อมแซม',
      'พร้อมขาย': 'พร้อมขาย',
      'ขายแล้ว': 'ขายแล้ว',
      'ติดจอง': 'ติดจอง',
      'new': 'ใหม่',
      'used': 'มือสอง',
      'repair': 'ซ่อมแซม'
    };

    // ตรวจสอบว่าข้อความมีตัวอักษรที่ผิดเพี้ยนหรือไม่ (รวมถึงรูปแบบต่างๆ)
    const hasGarbledChars = cleanText.includes('�') ||
      /[\uFFFD\uFEFF\u00BF\u00A0]/.test(cleanText) ||
      cleanText.includes('ï¿½') ||
      cleanText.includes('Ã¯Â¿Â½') ||
      /[\x00-\x1F\x7F-\x9F]/.test(cleanText);

    if (hasGarbledChars) {
      // สำหรับ BMS ที่มีตัวอักษรผิดเพี้ยนและมีคำว่า bluetooth
      if (cleanText.toLowerCase().includes('bluetooth')) {
        return 'มี bluetooth';
      }
      // สำหรับ Coulomb ที่มีตัวอักษรผิดเพี้ยน - ตรวจสอบความยาวเพื่อกำหนดประเภท
      else if (cleanText.length <= 6) {
        return 'ไร้สาย';
      } else {
        return 'มีสาย';
      }
    }

    // ตรวจสอบคำที่มี bluetooth ในข้อความ
    if (cleanText.toLowerCase().includes('bluetooth')) {
      return 'bluetooth';
    }

    return fixes[cleanText] || cleanText;
  };

  // ฟังก์ชันสำหรับแปลงข้อมูล CSV แบตเตอรี่
  const parseBatteryCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());

    if (lines.length < 2) {
      throw new Error('ไฟล์ต้องมีหัวตารางและข้อมูลอย่างน้อย 1 แถว');
    }

    // ตรวจสอบว่าใช้ separator อะไร
    let separator = ',';
    const firstLine = lines[0];

    if (firstLine.includes('\t')) {
      separator = '\t';
    } else if (firstLine.includes(',')) {
      separator = ',';
    } else if (firstLine.includes('  ')) {
      separator = /\s{2,}/;
    }

    const headers = separator instanceof RegExp
      ? lines[0].split(separator).map(h => h.trim()).filter(h => h)
      : lines[0].split(separator).map(h => h.trim());

    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = separator instanceof RegExp
        ? lines[i].split(separator).map(v => v.trim())
        : lines[i].split(separator).map(v => v.trim());

      const hasData = values.some(v => v && v.trim() !== '');

      if (hasData && values.length >= headers.length - 1) {
        const item = {};
        headers.forEach((header, index) => {
          let value = values[index] || '';
          if (value && typeof value === 'string' && value.trim() !== '') {
            if (header.toLowerCase().includes('coulomb') || header.toLowerCase().includes('คูลอมป์') ||
              header.toLowerCase().includes('bms') || header.toLowerCase().includes('bmsortype') || header.toLowerCase().includes('brand') ||
              header.toLowerCase().includes('condition') || header.toLowerCase().includes('status') ||
              header.toLowerCase().includes('note')) {
              value = fixGarbledText(value);
            }
          }
          const h = header.toLowerCase();
          let targetKey = header;
          if (h.includes('bms/type') || (h.includes('bms') && h.includes('type')) || h.includes('bmsortype')) {
            targetKey = 'bmsOrType';
          }
          item[targetKey] = value;
        });

        // Normalize ข้อมูลแบตเตอรี่
        const normalizedItem = normalizeBatteryData(item);

        // ตรวจสอบข้อมูลที่จำเป็น
        const hasRequiredData = normalizedItem.serial && normalizedItem.serial.trim() !== '' && normalizedItem.serial !== '-' &&
          normalizedItem.brand && normalizedItem.brand.trim() !== '' && normalizedItem.brand !== '-' &&
          normalizedItem.capacity && normalizedItem.capacity.trim() !== '' && normalizedItem.capacity !== '-';

        if (hasRequiredData) {
          data.push(normalizedItem);
        }
      }
    }

    return data;
  };

  // ฟังก์ชันสำหรับแปลงข้อมูล CSV ชาร์จเจอร์
  const parseChargerCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());

    if (lines.length < 2) {
      throw new Error('ไฟล์ต้องมีหัวตารางและข้อมูลอย่างน้อย 1 แถว');
    }

    // ตรวจสอบว่าใช้ separator อะไร
    let separator = ',';
    const firstLine = lines[0];

    if (firstLine.includes('\t')) {
      separator = '\t';
    } else if (firstLine.includes(',')) {
      separator = ',';
    } else if (firstLine.includes('  ')) {
      separator = /\s{2,}/;
    }

    const headers = separator instanceof RegExp
      ? lines[0].split(separator).map(h => h.trim()).filter(h => h)
      : lines[0].split(separator).map(h => h.trim());

    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = separator instanceof RegExp
        ? lines[i].split(separator).map(v => v.trim())
        : lines[i].split(separator).map(v => v.trim());

      const hasData = values.some(v => v && v.trim() !== '');

      if (hasData && values.length >= headers.length - 1) {
        const item = {};
        headers.forEach((header, index) => {
          let value = values[index] || '';
          if (value && typeof value === 'string' && value.trim() !== '') {
            if (header.toLowerCase().includes('type') || header.toLowerCase().includes('bms') || header.toLowerCase().includes('bmsortype') || header.toLowerCase().includes('brand') ||
              header.toLowerCase().includes('condition') || header.toLowerCase().includes('status') ||
              header.toLowerCase().includes('note')) {
              value = fixGarbledText(value);
            }
          }
          const h = header.toLowerCase();
          let targetKey = header;
          if (h.includes('bms/type') || (h.includes('bms') && h.includes('type')) || h.includes('bmsortype')) {
            targetKey = 'bmsOrType';
          }
          item[targetKey] = value;
        });

        // Normalize ข้อมูลชาร์จเจอร์
        const normalizedItem = normalizeChargerData(item);

        // ตรวจสอบข้อมูลที่จำเป็น
        const hasRequiredData = normalizedItem.serial && normalizedItem.serial.trim() !== '' && normalizedItem.serial !== '-' &&
          normalizedItem.brand && normalizedItem.brand.trim() !== '' && normalizedItem.brand !== '-' &&
          normalizedItem.capacity && normalizedItem.capacity.trim() !== '' && normalizedItem.capacity !== '-';

        if (hasRequiredData) {
          data.push(normalizedItem);
        }
      }
    }

    return data;
  };



  // ฟังก์ชันสำหรับตรวจสอบ duplicate entries
  const checkDuplicateEntries = async (data, productType) => {
    const duplicates = [];
    const uniqueData = [];

    for (const item of data) {
      try {
        // ตรวจสอบว่า serial มีอยู่แล้วหรือไม่
        const response = await fetch(`/api/${productType}-stock/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ serial: item.serial })
        });

        if (response.ok) {
          const results = await response.json();
          if (results.length > 0) {
            duplicates.push(item);
          } else {
            uniqueData.push(item);
          }
        } else {
          uniqueData.push(item); // ถ้าไม่สามารถตรวจสอบได้ ให้ผ่านไป
        }
      } catch (error) {
        uniqueData.push(item); // ถ้าเกิดข้อผิดพลาด ให้ผ่านไป
      }
    }

    return { duplicates, uniqueData };
  };

  // ฟังก์ชันสำหรับ import ข้อมูลทั้งหมด
  const handleBulkImport = async () => {
    // ใช้ข้อมูลจากพรีวิวที่ถูกแมป ถ้ามี; ถ้าไม่มีก็ใช้ importData เดิม
    const preparedRows = previewRows.length > 0
      ? mapRowsWithMapping(previewRows, columnMapping, currentImportType)
      : importData;

    if (!preparedRows || preparedRows.length === 0) {
      alert('ไม่มีข้อมูลสำหรับ import');
      return;
    }

    // ตรวจสอบ duplicate entries ก่อน
    const { duplicates, uniqueData } = await checkDuplicateEntries(preparedRows, currentImportType);

    let dataToImport = preparedRows;
    let shouldOverwrite = false;

    if (duplicates.length > 0) {
      const duplicateSerials = duplicates.map(d => d.serial).join(', ');
      const confirmMessage = `⚠️ พบข้อมูลซีเรียลซ้ำ ${duplicates.length} รายการ:\n${duplicateSerials}\n\n` +
        `เลือกการดำเนินการ:\n` +
        `• กด "ตกลง" เพื่อบันทึกทับข้อมูลเดิม (${duplicates.length} รายการ) และเพิ่มข้อมูลใหม่ (${uniqueData.length} รายการ)\n` +
        `• กด "ยกเลิก" เพื่อ import เฉพาะข้อมูลที่ไม่ซ้ำ (${uniqueData.length} รายการ)`;

      const userChoice = window.confirm(confirmMessage);

      if (userChoice) {
        // ผู้ใช้เลือกบันทึกทับข้อมูลเดิม
        shouldOverwrite = true;
        dataToImport = preparedRows; // import ทั้งหมด
      } else {
        // ผู้ใช้เลือก import เฉพาะข้อมูลที่ไม่ซ้ำ
        if (uniqueData.length === 0) {
          alert('ไม่มีข้อมูลใหม่ที่จะ import');
          return;
        }
        dataToImport = uniqueData;
      }
    }

    setIsImporting(true);
    setImportProgress(0);

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (let i = 0; i < dataToImport.length; i++) {
      const item = dataToImport[i];

      try {
        // แปลงตัวอักษรที่ผิดเพี้ยน
        const fixedItem = {
          ...item,
          coulomb: fixGarbledText(item.coulomb || item['คูลอมป์'] || ''),
          bmsOrType: fixGarbledText(item.bmsOrType || item.bms || item['BMS'] || item.type || ''),
          bms: fixGarbledText(item.bms || item.bmsOrType || item['BMS'] || item.type || '')
        };

        // ใช้ฟังก์ชัน formatDateForDB ที่ประกาศไว้ด้านบนแล้ว

        // เตรียมข้อมูลสำหรับบันทึก (ใช้ข้อมูลที่แก้ไขแล้วจาก fixedItem)
        const dataToSave = {
          serial: fixedItem.serial || '',
          brand: fixedItem.brand || '',
          capacity: parseInt(fixedItem.capacity) || '',
          bmsOrType: fixedItem.bmsOrType || fixedItem.bms || '',
          coulomb: fixedItem.coulomb || '',
          condition_status: toDBConditionStatus(fixedItem.condition || fixedItem.condition_status || 'ใหม่'),
          status: fixedItem.status || 'พร้อมขาย',
          entryDate: formatDateForDB(modalEntryDate || fixedItem.entryDate),
          note: fixedItem.note || '',
          reserver: fixedItem.reserver || '',
          receivedDate: formatDateForDB(fixedItem.receivedDate || fixedItem.entryDate)
        };

        // ตรวจสอบข้อมูลที่จำเป็น
        if (!dataToSave.serial || !dataToSave.brand || !dataToSave.capacity) {
          errors.push(`แถวที่ ${i + 1}: ข้อมูลไม่ครบถ้วน (serial, brand, capacity)`);
          errorCount++;
          continue;
        }

        // บันทึกข้อมูลตามประเภทสินค้า
        let result;
        if (currentImportType === 'battery') {
          result = await createBatteryStock(dataToSave, shouldOverwrite);
        } else {
          result = await createChargerStock(dataToSave, shouldOverwrite);
        }

        if (result.success) {
          successCount++;
        } else {
          // จัดการกับข้อผิดพลาด duplicate entry
          if (result.error && result.error.includes('Duplicate entry')) {
            if (shouldOverwrite) {
              // ถ้าเลือกให้บันทึกทับแต่ยังเกิดข้อผิดพลาด
              errors.push(`แถวที่ ${i + 1}: ไม่สามารถบันทึกทับ Serial ${dataToSave.serial} ได้`);
            } else {
              errors.push(`แถวที่ ${i + 1}: Serial ${dataToSave.serial} มีอยู่แล้วในระบบ`);
            }
          } else {
            errors.push(`แถวที่ ${i + 1}: ${result.error}`);
          }
          errorCount++;
        }
      } catch (error) {
        errors.push(`แถวที่ ${i + 1}: ${error.message}`);
        errorCount++;
      }

      // อัปเดต progress
      setImportProgress(Math.round(((i + 1) / dataToImport.length) * 100));

      // หน่วงเวลาเล็กน้อยเพื่อไม่ให้ระบบล้น
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setIsImporting(false);
    setShowImportModal(false);
    setImportData([]);
    setImportProgress(0);

    // แสดงผลลัพธ์
    let message = `Import เสร็จสิ้น!\n`;
    message += `สำเร็จ: ${successCount} รายการ\n`;
    message += `ล้มเหลว: ${errorCount} รายการ`;

    if (errors.length > 0 && errors.length <= 5) {
      message += `\n\nข้อผิดพลาด:\n${errors.join('\n')}`;
    } else if (errors.length > 5) {
      message += `\n\nข้อผิดพลาด (แสดง 5 รายการแรก):\n${errors.slice(0, 5).join('\n')}\n...และอีก ${errors.length - 5} รายการ`;
    }

    alert(message);

    // รีเซ็ต file input
    if (batteryFileInputRef.current) {
      batteryFileInputRef.current.value = '';
    }
    if (chargerFileInputRef.current) {
      chargerFileInputRef.current.value = '';
    }
  };



  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <div className="row mb-5">
        <div className="col-12 text-center">
          <div className="mb-4">
            <h1 className="display-5 fw-bold text-primary mb-3">
              <i className="fas fa-plus-circle me-3"></i>
              เพิ่มสินค้าใหม่
            </h1>
            <p className="lead text-muted">เพิ่มแบตเตอรี่และชาร์จเจอร์เข้าสู่ระบบสต๊อกอย่างง่ายดาย</p>
            <div className="d-inline-block">
              <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill">
                <i className="fas fa-info-circle me-2"></i>
                กรอกข้อมูลให้ครบถ้วนเพื่อความถูกต้อง
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="row g-4">
        {/* Form Section */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-lg h-100" style={{ borderRadius: '20px' }}>
            <div className="card-header bg-gradient text-white border-0 py-4" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '20px 20px 0 0'
            }}>
              <h4 className="text-center mb-0">
                <i className="fas fa-clipboard-list me-3"></i>
                ข้อมูลสินค้า
              </h4>
            </div>
            <div className="card-body p-5">
              <form onSubmit={handleSubmit}>
                {/* Product Type Selection */}
                <div className="mb-4">
                  <label className="form-label fw-semibold text-dark">
                    <i className="fas fa-tags me-2 text-primary"></i>
                    ประเภทสินค้า
                  </label>
                  <div className="row g-3">
                    <div className="col-6">
                      <div className={`card h-100 cursor-pointer border-2 ${productType === 'battery' ? 'border-primary bg-primary bg-opacity-10' : 'border-light'
                        }`} onClick={() => setProductType('battery')} style={{ cursor: 'pointer' }}>
                        <div className="card-body text-center py-3">
                          <i className={`fas fa-battery-full fa-2x mb-2 ${productType === 'battery' ? 'text-primary' : 'text-muted'
                            }`}></i>
                          <h6 className={`mb-0 ${productType === 'battery' ? 'text-primary fw-bold' : 'text-muted'
                            }`}>แบตเตอรี่</h6>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className={`card h-100 cursor-pointer border-2 ${productType === 'charger' ? 'border-success bg-success bg-opacity-10' : 'border-light'
                        }`} onClick={() => setProductType('charger')} style={{ cursor: 'pointer' }}>
                        <div className="card-body text-center py-3">
                          <i className={`fas fa-plug fa-2x mb-2 ${productType === 'charger' ? 'text-success' : 'text-muted'
                            }`}></i>
                          <h6 className={`mb-0 ${productType === 'charger' ? 'text-success fw-bold' : 'text-muted'
                            }`}>ชาร์จเจอร์</h6>
                        </div>
                      </div>
                    </div>
                  </div>
                  <input type="hidden" value={productType} onChange={handleTypeChange} />
                </div>

                {/* Basic Information Row */}
                <div className="row g-3 mb-4">
                  <div className="col-md-12">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-star me-2 text-warning"></i>
                      สภาพสินค้า
                    </label>
                    <select
                      name="condition"
                      value={formData.condition}
                      onChange={handleChange}
                      className="form-select form-select-lg border-2"
                      required
                    >
                      <option value="new">✨ ใหม่ (New)</option>
                      <option value="used">🔄 มือสอง (Used)</option>
                    </select>
                  </div>
                </div>

                {/* Serial and Brand Row */}
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-barcode me-2 text-secondary"></i>
                      ซีเรียล {productType === 'battery' ? 'แบตเตอรี่' : 'ชาร์จเจอร์'}
                    </label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-light border-2">
                        <i className="fas fa-hashtag text-muted"></i>
                      </span>
                      <input
                        type="text"
                        name="serial"
                        value={formData.serial}
                        onChange={handleChange}
                        className="form-control border-2"
                        placeholder="ป้อนหมายเลขซีเรียล"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-industry me-2 text-primary"></i>
                      ยี่ห้อ
                    </label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-light border-2">
                        <i className="fas fa-building text-muted"></i>
                      </span>
                      <input
                        type="text"
                        name="brand"
                        value={formData.brand}
                        onChange={handleChange}
                        className="form-control border-2"
                        placeholder="ป้อนยี่ห้อสินค้า"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Capacity and BMS/Type Row */}
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      <i className={`fas ${productType === 'battery' ? 'fa-bolt' : 'fa-tachometer-alt'} me-2 text-warning`}></i>
                      {productType === 'battery' ? 'ขนาด (Ah)' : 'ขนาด (A)'}
                    </label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-light border-2">
                        <i className="fas fa-calculator text-muted"></i>
                      </span>
                      <input
                        type="number"
                        name="capacity"
                        value={formData.capacity}
                        onChange={handleChange}
                        className="form-control border-2"
                        placeholder={`ป้อนขนาด${productType === 'battery' ? ' Ah' : ' A'}`}
                        required
                        min="0"
                        step="0.1"
                      />
                      <span className="input-group-text bg-light border-2 text-muted">
                        {productType === 'battery' ? 'Ah' : 'A'}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      <i className={`fas ${productType === 'battery' ? 'fa-microchip' : 'fa-cogs'} me-2 text-info`}></i>
                      {productType === 'battery' ? 'BMS' : 'ประเภท'}
                    </label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-light border-2">
                        <i className="fas fa-wrench text-muted"></i>
                      </span>
                      <input
                        type="text"
                        name="bmsOrType"
                        value={formData.bmsOrType}
                        onChange={handleChange}
                        className="form-control border-2"
                        placeholder={productType === 'battery' ? 'เช่น BMS Model X' : 'on board / off board'}
                      />
                    </div>
                  </div>
                </div>

                {/* Battery-specific field */}
                {productType === 'battery' && (
                  <div className="mb-4">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-plug me-2 text-success"></i>
                      คูลอมป์
                    </label>
                    <div className="row g-3">
                      <div className="col-6">
                        <div className={`card h-100 cursor-pointer border-2 ${formData.coulomb === 'มีสาย' ? 'border-success bg-success bg-opacity-10' : 'border-light'
                          }`} onClick={() => setFormData(prev => ({ ...prev, coulomb: 'มีสาย' }))} style={{ cursor: 'pointer' }}>
                          <div className="card-body text-center py-3">
                            <i className={`fas fa-link fa-lg mb-2 ${formData.coulomb === 'มีสาย' ? 'text-success' : 'text-muted'
                              }`}></i>
                            <h6 className={`mb-0 ${formData.coulomb === 'มีสาย' ? 'text-success fw-bold' : 'text-muted'
                              }`}>มีสาย</h6>
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className={`card h-100 cursor-pointer border-2 ${formData.coulomb === 'ไร้สาย' ? 'border-info bg-info bg-opacity-10' : 'border-light'
                          }`} onClick={() => setFormData(prev => ({ ...prev, coulomb: 'ไร้สาย' }))} style={{ cursor: 'pointer' }}>
                          <div className="card-body text-center py-3">
                            <i className={`fas fa-wifi fa-lg mb-2 ${formData.coulomb === 'ไร้สาย' ? 'text-info' : 'text-muted'
                              }`}></i>
                            <h6 className={`mb-0 ${formData.coulomb === 'ไร้สาย' ? 'text-info fw-bold' : 'text-muted'
                              }`}>ไร้สาย</h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}



                {/* Date and Notes Row */}
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-calendar-alt me-2 text-success"></i>
                      วันรับเข้า
                    </label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-light border-2">
                        <i className="fas fa-calendar text-muted"></i>
                      </span>
                      <input
                        type="date"
                        name="entryDate"
                        value={formData.entryDate}
                        onChange={handleChange}
                        className="form-control border-2"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-sticky-note me-2 text-secondary"></i>
                      หมายเหตุ
                    </label>
                    <textarea
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      className="form-control border-2"
                      rows="3"
                      placeholder="ข้อมูลเพิ่มเติม (ไม่บังคับ)"
                      style={{ resize: 'none' }}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className={`btn btn-lg shadow-sm ${productType === 'battery' ? 'btn-primary' : 'btn-success'
                      }`}
                    disabled={loading}
                    style={{
                      background: productType === 'battery'
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      border: 'none',
                      borderRadius: '12px'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        กำลังบันทึก...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-plus-circle me-2"></i>
                        เพิ่ม{productType === 'battery' ? 'แบตเตอรี่' : 'ชาร์จเจอร์'}
                      </>
                    )}
                  </button>
                </div>

                <input
                  type="file"
                  ref={batteryFileInputRef}
                  accept=".csv,.tsv,.txt,.xlsx,.xls"
                  onChange={handleBatteryFileUpload}
                  style={{ display: 'none' }}
                />
                <input
                  type="file"
                  ref={chargerFileInputRef}
                  accept=".csv,.tsv,.txt,.xlsx,.xls"
                  onChange={handleChargerFileUpload}
                  style={{ display: 'none' }}
                />
              </form>
            </div>
          </div>
        </div>

        {/* Import Section */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-lg h-100" style={{ borderRadius: '20px' }}>
            <div className="card-header bg-gradient text-white border-0 py-4" style={{
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              borderRadius: '20px 20px 0 0'
            }}>
              <h4 className="text-center mb-0">
                <i className="fas fa-file-import me-3"></i>
                นำเข้าข้อมูลจำนวนมาก
              </h4>
            </div>
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <p className="text-muted mb-4">นำเข้าข้อมูลหลายรายการพร้อมกันด้วยไฟล์ CSV หรือ Excel</p>
                <div className="d-inline-block">
                  <span className="badge bg-info bg-opacity-10 text-info px-3 py-2 rounded-pill">
                    <i className="fas fa-lightbulb me-2"></i>
                    รองรับไฟล์ CSV, TSV, TXT, XLSX และ XLS
                  </span>
                </div>
              </div>

              {/* Battery Import Section */}
              {productType === 'battery' && (
                <div className="mb-5" data-section="battery-import">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                      <i className="fas fa-battery-full text-primary"></i>
                    </div>
                    <h5 className="mb-0 text-primary fw-bold">นำเข้าข้อมูลแบตเตอรี่</h5>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <button
                        type="button"
                        className="btn btn-outline-primary w-100 py-3 border-2"
                        onClick={downloadBatterySampleCSV}
                        style={{ borderRadius: '12px' }}
                        disabled={productType !== 'battery'}
                      >
                        <i className="fas fa-download me-2"></i>
                        <div className="small">ดาวน์โหลด</div>
                        <div className="fw-bold">ไฟล์ตัวอย่าง</div>
                      </button>
                    </div>
                    <div className="col-6">
                      <button
                        type="button"
                        className="btn btn-primary w-100 py-3"
                        onClick={() => batteryFileInputRef.current?.click()}
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          border: 'none',
                          borderRadius: '12px'
                        }}
                        disabled={productType !== 'battery'}
                      >
                        <i className="fas fa-upload me-2"></i>
                        <div className="small">เลือกไฟล์</div>
                        <div className="fw-bold">นำเข้าข้อมูล</div>
                      </button>
                    </div>
                  </div>

                  <div className="bg-light rounded p-3">
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      รูปแบบ: Serial, Brand, Capacity, BMS, Coulomb, Condition, EntryDate, Note
                    </small>
                  </div>
                </div>
              )}

              {/* Charger Import Section */}
              {productType === 'charger' && (
                <div className="mb-4" data-section="charger-import">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-success bg-opacity-10 rounded-circle p-2 me-3">
                      <i className="fas fa-plug text-success"></i>
                    </div>
                    <h5 className="mb-0 text-success fw-bold">นำเข้าข้อมูลชาร์จเจอร์</h5>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <button
                        type="button"
                        className="btn btn-outline-success w-100 py-3 border-2"
                        onClick={downloadChargerSampleCSV}
                        style={{ borderRadius: '12px' }}
                        disabled={productType !== 'charger'}
                      >
                        <i className="fas fa-download me-2"></i>
                        <div className="small">ดาวน์โหลด</div>
                        <div className="fw-bold">ไฟล์ตัวอย่าง</div>
                      </button>
                    </div>
                    <div className="col-6">
                      <button
                        type="button"
                        className="btn btn-success w-100 py-3"
                        onClick={() => chargerFileInputRef.current?.click()}
                        style={{
                          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          border: 'none',
                          borderRadius: '12px'
                        }}
                        disabled={productType !== 'charger'}
                      >
                        <i className="fas fa-upload me-2"></i>
                        <div className="small">เลือกไฟล์</div>
                        <div className="fw-bold">นำเข้าข้อมูล</div>
                      </button>
                    </div>
                  </div>

                  <div className="bg-light rounded p-3">
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      รูปแบบ: Serial, Brand, Capacity, Type, Condition, EntryDate, Note
                    </small>
                  </div>
                </div>
              )}

              {/* Tips Section */}
              <div className="alert alert-info border-0" style={{ backgroundColor: '#e3f2fd', borderRadius: '12px' }}>
                <h6 className="alert-heading text-info">
                  <i className="fas fa-tips me-2"></i>
                  เคล็ดลับการใช้งาน
                </h6>
                <ul className="mb-0 small text-info">
                  <li>ดาวน์โหลดไฟล์ตัวอย่างก่อนเพื่อดูรูปแบบที่ถูกต้อง</li>
                  <li>ตรวจสอบข้อมูลให้ครบถ้วนก่อนนำเข้า</li>
                  <li>ระบบจะแจ้งเตือนหากมีข้อมูลซ้ำ</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && createPortal(
        <div
          tabIndex="-1"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1055,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isImporting) {
              setShowImportModal(false);
            }
          }}
        >
          <div
            style={{
              width: '85vw',
              maxWidth: '85vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              background: '#fff',
              borderRadius: '10px',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{
              width: '100%',
              maxWidth: 'none',
              borderRadius: '10px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}>
              <div
                className="modal-header"
                style={{
                  background: currentImportType === 'battery'
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: '#fff',
                  border: 'none'
                }}
              >
                <h5 className="modal-title">
                  <i className={`fas ${currentImportType === 'battery' ? 'fa-battery-full' : 'fa-plug'} me-2`}></i>
                  ตรวจสอบข้อมูล{currentImportType === 'battery' ? 'แบตเตอรี่' : 'ชาร์จเจอร์'}ก่อน Import
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowImportModal(false)}
                  disabled={isImporting}
                ></button>
              </div>
              <div className="modal-body" style={{
                flex: 1,
                overflowY: 'auto',
                padding: '20px'
              }}>
                {isImporting ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5>กำลัง Import ข้อมูล...</h5>
                    <div className="progress mt-3" style={{ height: '20px' }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${importProgress}%` }}
                      >
                        {importProgress}%
                      </div>
                    </div>
                  </div>
                ) : (
                  <>

                    <div className={`alert ${currentImportType === 'battery' ? 'alert-primary' : 'alert-success'} mb-4`}>
                      <i className="fas fa-info-circle me-2"></i>
                      เลือกแมปคอลัมน์ให้ตรงกับฟิลด์ฐานข้อมูล แล้วตรวจสอบตัวอย่างข้อมูล 10 แรก
                    </div>

                    {previewRows.length > 0 && (
                      <div className="card border-0 shadow-sm mb-3">
                        <div className="card-header bg-transparent">
                          <h6 className="fw-bold mb-0">
                            <i className="fas fa-columns me-2"></i>
                            การแมปคอลัมน์
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="row g-2">
                            {[
                              { key: 'serial', label: 'Serial (จำเป็น)' },
                              { key: 'brand', label: 'Brand (จำเป็น)' },
                              { key: 'capacity', label: 'Capacity (จำเป็น)' },
                              { key: 'bmsOrType', label: currentImportType === 'battery' ? 'BMS' : 'Type' },
                              ...(currentImportType === 'battery' ? [{ key: 'coulomb', label: 'Coulomb' }] : []),
                              { key: 'condition', label: 'Condition' },
                              { key: 'status', label: 'Status' },
                              { key: 'entryDate', label: 'Entry Date' },
                              { key: 'receivedDate', label: 'Received Date / วันรับเข้า' },
                              { key: 'note', label: 'Note' }
                            ].map((f) => (
                              <div className="col-sm-6 col-md-4 col-lg-3 col-xl-2-4" key={f.key} style={{
                                flex: '0 0 auto',
                                width: 'calc(100% / 5)'
                              }}>
                                <label className="form-label small">{f.label}</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={columnMapping[f.key] || ''}
                                  onChange={(e) => setColumnMapping((prev) => ({ ...prev, [f.key]: e.target.value }))}
                                >
                                  <option value="">— ไม่ใช้/ไม่แมป —</option>
                                  {previewHeaders.map((h) => (
                                    <option key={h} value={h}>{h}</option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {previewRows.length > 0 && (
                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-transparent">
                          <h6 className="fw-bold mb-0">ตัวอย่างข้อมูลจากไฟล์ (10 แถวแรก)</h6>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '40vh' }}>
                          <table className="table table-striped table-hover">
                            <thead className="table-secondary sticky-top">
                              <tr>
                                <th>#</th>
                                {previewHeaders.map((h) => (<th key={h}>{h}</th>))}
                              </tr>
                            </thead>
                            <tbody>
                              {previewRows.slice(0, 10).map((row, idx) => (
                                <tr key={idx}>
                                  <td>{idx + 1}</td>
                                  {previewHeaders.map((h) => (<td key={h}>{row[h]}</td>))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {previewRows.length > 0 && (
                      <div className="card border-0 shadow-sm mb-2">
                        <div className="card-header bg-transparent">
                          <h6 className="fw-bold mb-0">ตัวอย่างข้อมูลหลังแมป (10 แถวแรก)</h6>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '40vh' }}>
                          <table className="table table-striped table-hover">
                            <thead className="table-dark sticky-top">
                              <tr>
                                <th>#</th>
                                <th>Serial</th>
                                <th>Brand</th>
                                <th>Capacity</th>
                                <th>{currentImportType === 'battery' ? 'BMS' : 'Type'}</th>
                                {currentImportType === 'battery' && <th>Coulomb</th>}
                                <th>Condition</th>
                                <th>Status</th>
                                <th>Entry Date</th>
                                <th>Received Date</th>
                                <th>Note</th>
                              </tr>
                            </thead>
                            <tbody>
                              {mapRowsWithMapping(previewRows.slice(0, 10), columnMapping, currentImportType, modalEntryDate).map((item, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.serial || '-'}</td>
                                  <td>{fixGarbledText(item.brand || '-')}</td>
                                  <td>{item.capacity || '-'}</td>
                                  <td>{fixGarbledText(item.bmsOrType || item.bms || item['BMS'] || item.type || '') || '-'}</td>
                                  {currentImportType === 'battery' && (
                                    <td>{fixGarbledText(item.coulomb || item['คูลอมป์'] || '') || 'มีสาย'}</td>
                                  )}
                                  <td>
                                    <span className={`badge ${(fixGarbledText(item.condition) || 'ใหม่') === 'ใหม่' ? 'bg-success' : 'bg-warning'
                                      }`}>
                                      {fixGarbledText(item.condition) || 'ใหม่'}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`badge ${(item.status || 'พร้อมขาย') === 'พร้อมขาย' ? 'bg-primary' : 'bg-secondary'
                                      }`}>
                                      {fixGarbledText(item.status || 'พร้อมขาย')}
                                    </span>
                                  </td>
                                  <td>{formatDateForDisplay(item.entryDate || modalEntryDate) || '-'}</td>
                                  <td>{formatDateForDisplay(item.receivedDate || item.entryDate || modalEntryDate) || '-'}</td>
                                  <td>{fixGarbledText(item.note || '-')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {previewRows.length === 0 && (
                      <div className="table-responsive" style={{ maxHeight: '400px' }}>
                        <table className="table table-striped table-hover">
                          <thead className="table-dark sticky-top">
                            <tr>
                              <th>#</th>
                              <th>Serial</th>
                              <th>Brand</th>
                              <th>Capacity</th>
                              <th>{currentImportType === 'battery' ? 'BMS' : 'Type'}</th>
                              {currentImportType === 'battery' && <th>Coulomb</th>}
                              <th>Condition</th>
                              <th>Status</th>
                              <th>Entry Date</th>
                              <th>Received Date</th>
                              <th>Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importData.slice(0, 10).map((item, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{item.serial || '-'}</td>
                                <td>{fixGarbledText(item.brand || '-')}</td>
                                <td>{item.capacity || '-'}</td>
                                <td>{fixGarbledText(item.bmsOrType || item.bms || item['BMS'] || item.type || '') || '-'}</td>
                                {currentImportType === 'battery' && (
                                  <td>{fixGarbledText(item.coulomb || item['คูลอมป์'] || '') || 'มีสาย'}</td>
                                )}
                                <td>
                                  <span className={`badge ${(fixGarbledText(item.condition) || 'ใหม่') === 'ใหม่' ? 'bg-success' : 'bg-warning'
                                    }`}>
                                    {fixGarbledText(item.condition) || 'ใหม่'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${(item.status || 'พร้อมขาย') === 'พร้อมขาย' ? 'bg-primary' : 'bg-secondary'
                                    }`}>
                                    {fixGarbledText(item.status || 'พร้อมขาย')}
                                  </span>
                                </td>
                                <td>{formatDateForDisplay(item.entryDate) || '-'}</td>
                                <td>{formatDateForDisplay(item.receivedDate || item.entryDate) || '-'}</td>
                                <td>{fixGarbledText(item.note || '-')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImportModal(false)}
                  disabled={isImporting}
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleBulkImport}
                  disabled={isImporting || (previewRows.length === 0 && importData.length === 0)}
                >
                  <i className="fas fa-upload me-2"></i>
                  Import ทั้งหมด ({previewRows.length > 0 ? previewRows.length : importData.length} รายการ)
                </button>
              </div>
            </div>
          </div>
        </div>
        ,
        document.body
      )}
    </div>
  );
};

export default AddProductForm;
